"use client";

import { memo } from "react";
import { useTranslations } from "next-intl";
import type { ActivityDataPoint } from "./types";
import { ACTIVITY_CHART_COLORS } from "./constants";

interface ActivityTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: ActivityDataPoint }>;
  label?: string | number;
  displayMode?: "minutes" | "questions";
}

/**
 * Определяет цвет для точки данных
 */
function getPointColor(point: ActivityDataPoint): string {
  if (point.isBurst) return ACTIVITY_CHART_COLORS.burst;
  if (!point.isNormal) return ACTIVITY_CHART_COLORS.exceeded;
  return ACTIVITY_CHART_COLORS.normal;
}

/**
 * Кастомный Tooltip для графика активности
 */
export const ActivityTooltip = memo(function ActivityTooltip({
  active,
  payload,
  label,
  displayMode = "minutes",
}: ActivityTooltipProps) {
  const t = useTranslations();

  if (!active || !payload?.length) {
    return null;
  }

  const data = payload[0];
  const points = data.value;
  const point = data.payload;
  const isBurst = point.isBurst;

  // Функция для форматирования времени в секундах в читаемый вид
  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const xLabel = displayMode === "questions" ? t("label-question") : t("label-minute");
  const valueLabel = displayMode === "questions" ? formatTime(points) : `${points} ${t("label-max-activity")}`;

  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md">
      <p className="text-sm font-medium">
        {xLabel} {point.minute}
      </p>
      <div className="flex items-center gap-2 mt-1">
        <span
          className="w-3 h-3 rounded-sm"
          style={{
            backgroundColor: getPointColor(point),
          }}
        />
        <span className="text-sm">
          <span className="font-semibold">{displayMode === "questions" ? formatTime(points) : `${points} ${t('label-answers')}`}</span>
        </span>
      </div>
      {isBurst && (
        <p className="text-xs text-amber-600 mt-1 font-medium">
          {t("label-suspicious-burst")}
        </p>
      )}
    </div>
  );
});
